import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { UPLOAD } from "@/constants/testIds";
import { uploadBill, createBill } from "@/lib/api";
import { useAccounts } from "@/contexts/AccountContext";
import { toast } from "sonner";

function fieldRenderer({ label, testid, type = "text", value, onChange, step, ...rest }) {
  return (
    <label className="block">
      <div className="overline mb-2">{label}</div>
      <input
        data-testid={testid}
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E5E5DE] px-4 py-3 font-mono-data text-sm focus:outline-none focus:border-[#0F3524]"
        {...rest}
      />
    </label>
  );
}
const Field = fieldRenderer;

export default function Upload() {
  const nav = useNavigate();
  const { selectedId } = useAccounts();
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const bill = await uploadBill(file, selectedId);
      toast.success(`Parsed ${bill.month_label}: ${bill.kwh_consumed} kWh · RM ${bill.total_cost_rm.toFixed(2)}`);
      nav("/dashboard");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <div data-testid={UPLOAD.page} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="mb-10">
          <div className="overline mb-3">Field entry</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">
            File a new bill.
          </h1>
          <p className="mt-4 text-[#6E716C] max-w-xl">
            Drop a TNB PDF or photo (JPG/PNG) of your bill — we&apos;ll parse the fields with Claude Sonnet 4.5. Or type them in by hand.
          </p>
        </div>

        <div
          data-testid={UPLOAD.dropzone}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer?.files?.[0];
            if (f) onFile(f);
          }}
          className={`border-2 border-dashed ${drag ? "border-[#0F3524] bg-[#F0F0EA]" : "border-[#C5C5BE] bg-[#FAFAF7]"} p-12 sm:p-16 text-center transition-colors`}
        >
          <div className="font-display text-2xl mb-2">Drop your bill here</div>
          <div className="text-sm text-[#6E716C] mb-6">PDF or photo (JPG · PNG) — click to browse</div>
          <input
            data-testid={UPLOAD.fileInput}
            ref={inputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/jpg,image/png,.pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <button
            data-testid={UPLOAD.submitBtn}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="px-6 py-3 bg-[#0F3524] text-[#F7F7F4] hover:bg-[#174D35] transition-colors disabled:opacity-60"
          >
            {busy ? "Parsing with Claude…" : "Choose file"}
          </button>
        </div>

        <div className="mt-14">
          <div className="overline mb-3">Prefer manual?</div>
          <h2 className="font-display text-2xl mb-6">Enter the numbers by hand</h2>
          <ManualForm onDone={() => nav("/dashboard")} />
        </div>
      </div>
    </Layout>
  );
}

function ManualForm({ onDone }) {
  const [state, setState] = useState({
    month_label: "",
    period_start: "",
    period_end: "",
    days_in_period: 30,
    kwh_consumed: 0,
    kwh_exported: 0,
    peak_demand_kw: 0,
    power_factor: 0,
    total_cost_rm: 0,
    generation_rm: 0,
    network_rm: 0,
    retail_rm: 0,
    afa_rm: 0,
    kwtbb_rm: 0,
    tariff_name: "",
    account_number: "",
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createBill({
        ...state,
        days_in_period: Number(state.days_in_period),
        kwh_consumed: Number(state.kwh_consumed),
        kwh_exported: Number(state.kwh_exported) || 0,
        peak_demand_kw: Number(state.peak_demand_kw) || 0,
        power_factor: Number(state.power_factor) || 0,
        total_cost_rm: Number(state.total_cost_rm),
        generation_rm: Number(state.generation_rm),
        network_rm: Number(state.network_rm),
        retail_rm: Number(state.retail_rm),
        afa_rm: Number(state.afa_rm),
        kwtbb_rm: Number(state.kwtbb_rm),
      });
      toast.success("Bill saved");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save bill");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form data-testid={UPLOAD.manualForm} onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Field label="Month label" testid={UPLOAD.manualMonth} value={state.month_label} onChange={(v) => set("month_label", v)} placeholder="e.g. Feb 2026" />
      <Field label="Period start" testid={UPLOAD.manualStart} type="date" value={state.period_start} onChange={(v) => set("period_start", v)} />
      <Field label="Period end" testid={UPLOAD.manualEnd} type="date" value={state.period_end} onChange={(v) => set("period_end", v)} />
      <Field label="Days in period" testid={UPLOAD.manualDays} type="number" value={state.days_in_period} onChange={(v) => set("days_in_period", v)} />
      <Field label="kWh consumed" testid={UPLOAD.manualKwh} type="number" step="0.01" value={state.kwh_consumed} onChange={(v) => set("kwh_consumed", v)} />
      <Field label="Total cost (RM)" testid={UPLOAD.manualCost} type="number" step="0.01" value={state.total_cost_rm} onChange={(v) => set("total_cost_rm", v)} />
      <div className="sm:col-span-2 mt-4 pt-4 border-t border-[#E5E5DE]">
        <div className="overline mb-1">Optional — commercial tariff bills</div>
        <p className="text-xs text-[#6E716C] mb-4">Fill these only if your bill shows them (medium/high voltage tariff or NEM export).</p>
      </div>
      <Field label="kWh exported (NEM / solar)" testid="manual-kwh-exported" type="number" step="0.01" value={state.kwh_exported} onChange={(v) => set("kwh_exported", v)} />
      <Field label="Peak demand (kW)" testid="manual-peak-demand" type="number" step="0.01" value={state.peak_demand_kw} onChange={(v) => set("peak_demand_kw", v)} />
      <Field label="Power factor (0–1, e.g. 0.96)" testid="manual-power-factor" type="number" step="0.01" value={state.power_factor} onChange={(v) => set("power_factor", v)} />
      <div className="sm:col-span-2 pt-2">
        <button
          data-testid={UPLOAD.manualSubmit}
          type="submit"
          disabled={busy}
          className="px-6 py-3 bg-[#0F3524] text-[#F7F7F4] hover:bg-[#174D35] transition-colors disabled:opacity-60"
        >
          {busy ? "Saving…" : "Save bill"}
        </button>
      </div>
    </form>
  );
}
